// OPG CoPilot combo pricing (CR-20260710-001, WI-18 / amendment A10).
//
// DUPLICATED from hedging-grid/copilot/copilot-pricing.ts per the CoPilot family's isolated-sibling
// rule (duplicate, never import across panel folders). Deliberate divergences from that donor:
//   - no per-leg ES->SPX normFactor: a Hedge solution can mix SPX and ES legs in ONE combo; the OPG
//     panel prices a single underlying at a time, so there is nothing to normalize per leg.
//   - the caller resolves strikes/expirations/quotes itself (the OPG mold is strike-less until the
//     startingStrike walk resolves it) and hands this module already-priced legs.
// findHCF comes from the platform utils — the SAME function PricingGridStrategyColumn uses, which is
// what makes the ledger price below agree with the grid by construction rather than by coincidence.
//
// ---- THE TWO PRICES (owner ruling, 2026-07-14) ---------------------------------------------------
//
// The vocabulary is the platform's, not ours ("pick up that terminology from the other places in the
// software"):
//
//   LEDGER price = net / defaultQty   — "the price normalized to the default quantity".
//       Rendered as the card's top-right headline (unlabelled — it is simply THE price of the trade).
//       It MUST equal the OPG grid's strategy-column price. That is not an aspiration, it is
//       arithmetic: the grid takes the raw strategy price (which IS the order price, net / HCF) and
//       multiplies it by hcfMultiplier = HCF / defaultQty, so the HCF cancels and the grid cell holds
//       net / defaultQty (pricing-grid-strategy.column.ts:307-319).
//
//   ORDER price  = net / HCF          — the minimal, HCF-reduced combo, with NO default-quantity
//       normalization ("order price without normalizing to the default quantity"). Rendered in the
//       expanded legs table's "Total (Order Price)" footer — the Hedge solution card's own footer,
//       which is where this whole shape was copied from. It is NOT required to equal the ledger price;
//       it just usually does. See below.
//
// ---- WHY THEY USUALLY AGREE, AND WHEN THEY DO NOT ------------------------------------------------
//
// Since contract v5 (CR-20260827-002) a trade's ACTUAL leg quantities ARRIVE ABSOLUTE from the
// engine — its covering multiplier m is the executing contract count, and neither the card nor
// the grid multiplies by Default Qty any more (ORION-loaded strategies carry the descriptor's
// absoluteQty marker past pricing-grid-strategy.column.ts's scaling). With absolute quantities
// q_i = base_i × m (base = the structure's per-leg ratio, normally coprime):
//
//       HCF(actual) == m × HCF(base)
//       order       == net / HCF(actual)
//       ledger      == net / defaultQty
//
// The two are DIFFERENT numbers whenever m ≠ defaultQty × HCF(base) — routine now that the engine
// sizes m off the dollar target (a $7,000 target on a 5-wide ladder is m = 14 at any Default
// Qty). Both are correct; do not "fix" the divergence. The grid-match invariant is only about
// the LEDGER price: the grid column computes (net / HCF) × (HCF / defaultQty), the HCF cancels,
// and the cell holds net / defaultQty — the same number opgLedgerPrice returns.
//
// ---- FEED IT ACTUAL QUANTITIES -------------------------------------------------------------------
//
// Everything above assumes `legs` carry the quantities the trade is actually placed in — which,
// since v5, are the engine's own leg quantities verbatim. Re-scaling them by Default Qty (the
// pre-v5 behaviour) would price a combo defaultQty× the one that executes and break the
// headline's match with the grid cell.

import { findHCF, isValidNumber } from '../utils';

// One leg of the trade, resolved and priced: the side, the ACTUAL quantity it trades in (always
// positive — direction lives in `side`), and the leg's live quote mid.
export interface OpgPricedLeg {
    side: 'Buy' | 'Sell';
    qty: number;
    mid: number;
}

// The two pieces every combo price is derived from: the signed net and the HCF of the leg quantities.
export interface OpgComboNet {
    net: number;
    hcf: number;
}

// Per-leg signed cash flow: a SELL receives premium (credit, +), a BUY pays it (debit, -). The same
// sign rule the combo net below uses and the same one the Hedge donor's legSignedPrice uses — so a
// leg's own row and the total it rolls up into can never disagree about direction.
export function opgLegSignedPrice(leg: OpgPricedLeg): number | null {
    if (!leg || !isValidNumber(leg.mid)) {
        return null;
    }
    return leg.side === 'Sell' ? leg.mid : -leg.mid;
}

// net = sum of signed (Sell:+ / Buy:-) mid * |qty|, plus the HCF of the leg quantities.
// ALL-OR-NOTHING (the platform getGroupTotal rule): null unless every leg carries a usable quantity
// and a usable mid — a partially-priced combo is never a price, it is a blank.
export function computeOpgComboNet(legs: OpgPricedLeg[] | null | undefined): OpgComboNet | null {
    if (!legs || legs.length === 0) {
        return null;
    }
    let net = 0;
    for (let i = 0; i < legs.length; i++) {
        const leg = legs[i];
        if (!isValidNumber(leg.qty, true) || !isValidNumber(leg.mid)) {
            return null;   // falseOnZero on qty: a 0-quantity leg is degenerate, never invent one
        }
        const signed = leg.side === 'Sell' ? 1 : -1;
        net += signed * leg.mid * Math.abs(leg.qty);
    }
    const hcf = findHCF(legs.map(l => Math.abs(l.qty)));
    return { net, hcf };
}

// Order price = net / HCF — the minimal HCF-reduced combo, NO default-quantity normalization.
// The expanded table's "Total (Order Price)" row.
export function opgOrderPrice(combo: OpgComboNet | null | undefined): number | null {
    if (!combo || !isValidNumber(combo.hcf, true)) {
        return null;
    }
    return combo.net / combo.hcf;
}

// Ledger price = net / defaultQty — the price normalized to the default quantity. The card's headline,
// and the number that must agree with the grid's strategy-column price.
// Without a usable defaultQty there is nothing to normalize TO, so it degrades to the order price
// (net / HCF) — the same fallback the Hedge donor makes.
export function opgLedgerPrice(combo: OpgComboNet | null | undefined, defaultQty: number | null | undefined): number | null {
    if (!combo) {
        return null;
    }
    if (!isValidNumber(defaultQty, true) || defaultQty <= 0) {
        return opgOrderPrice(combo);
    }
    return combo.net / defaultQty;
}
